import { AxiosError } from 'axios';
import i18n from '@/shared/lib/i18n';
import type { ApiError } from '@/shared/types/api/common';

export const isApiError = (err: unknown): err is AxiosError<ApiError> => err instanceof AxiosError;

export const extractErrorMessage = (err: unknown, fallback?: string): string => {
  const resolvedFallback = fallback ?? i18n.t('common.errorGeneric');
  if (!isApiError(err)) {
    if (err instanceof Error) return err.message;
    return resolvedFallback;
  }
  const data = err.response?.data as Record<string, unknown> | undefined;
  if (!data) return err.message || resolvedFallback;
  // Backend-shaped {"message": "..."} — preferred.
  if (typeof data.message === 'string') return data.message;
  if (typeof data.detail === 'string') return data.detail;
  // {"errors": {field: [msg]}} envelope.
  if (data.errors && typeof data.errors === 'object') {
    const first = Object.values(data.errors as Record<string, unknown>)[0];
    if (Array.isArray(first) && first.length > 0 && typeof first[0] === 'string') return first[0];
    if (typeof first === 'string') return first;
  }
  // DRF default — root-level field errors. ``non_field_errors`` first, then
  // the first listed field. Each value is typically an array of strings.
  const drf = data as Record<string, unknown>;
  if (Array.isArray(drf.non_field_errors) && drf.non_field_errors.length > 0
      && typeof drf.non_field_errors[0] === 'string') {
    return drf.non_field_errors[0] as string;
  }
  for (const [key, value] of Object.entries(drf)) {
    if (key === 'message' || key === 'detail' || key === 'errors') continue;
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
      return `${key}: ${value[0]}`;
    }
    if (typeof value === 'string') return value;
  }
  return err.message || resolvedFallback;
};

export const extractFieldErrors = (err: unknown): Record<string, string> => {
  if (!isApiError(err)) return {};
  const errors = err.response?.data?.errors;
  if (!errors) return {};
  const out: Record<string, string> = {};
  for (const [field, messages] of Object.entries(errors)) {
    if (Array.isArray(messages) && messages.length > 0 && typeof messages[0] === 'string') {
      out[field] = messages[0];
    }
  }
  return out;
};
